// =================================================================
// ConnectorActivationModal.js — Activation d'un connecteur sur un projet
// Session S7 (20 mai 2026) — F1 frontend connecteurs V1
// =================================================================
// Modale légère pour activer un connecteur (V1 : N8N uniquement).
// Form : webhook_url → POST /api/connectors/projects/:projectId/:code
// Une fois activé, le LLM peut invoquer le connecteur via le tool
// `invoke_connector` (mode project). Pas de "trigger direct" UI : la
// philosophie A1.c veut que l'invocation passe par le chat.
// =================================================================

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Zap, CheckCircle2, Activity, Unplug } from 'lucide-react';

// Libellés UI (valeurs techniques inchangées en base)
const SITE_TYPE_LABELS = {
  mosquee: 'Association',
  webradio: 'Média',
  formation: 'Entreprise & formations',
};

const ConnectorActivationModal = ({
  projectId,
  connectorCode,
  connectorName,
  alreadyActive = false,
  onClose,
  onActivated,
  onDeactivated,
}) => {
  // S2 — Bouton « Déconnecter » : appel DELETE sur le connecteur déjà actif
  const [isDeactivating, setIsDeactivating] = useState(false);

  const handleDeactivate = async () => {
    if (!window.confirm(`Déconnecter ${connectorName} du projet ? Le token chiffré sera supprimé (soft delete, audit log conservé).`)) return;
    setIsDeactivating(true);
    try {
      const token = localStorage.getItem('oracle_token');
      await axios.delete(
        `/api/connectors/projects/${projectId}/${connectorCode}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (onDeactivated) onDeactivated();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Désactivation échouée');
    } finally {
      setIsDeactivating(false);
    }
  };

  // n8n
  const [webhookUrl, setWebhookUrl] = useState('');
  // site_internet
  const [baseUrl, setBaseUrl] = useState('');
  const [siteType, setSiteType] = useState('webradio');
  const [apiUsername, setApiUsername] = useState('');
  const [apiPassword, setApiPassword] = useState('');
  const [defaultRubrique, setDefaultRubrique] = useState('');
  // email (P7 — 2e cercle)
  const [imapHost, setImapHost] = useState('');
  const [imapPort, setImapPort] = useState(993);
  const [imapSecure, setImapSecure] = useState(true);
  const [smtpHost, setSmtpHost] = useState('');
  const [smtpPort, setSmtpPort] = useState(465);
  const [smtpSecure, setSmtpSecure] = useState(true);
  const [fromEmail, setFromEmail] = useState('');
  const [fromName, setFromName] = useState('');
  const [signatureHtml, setSignatureHtml] = useState('');
  const [tlsServername, setTlsServername] = useState('');
  const [imapUser, setImapUser] = useState('');
  const [imapPassword, setImapPassword] = useState('');
  // wordpress_source (ingestion WP read-only)
  const [accessMode, setAccessMode] = useState('rest_public');
  const [wpUsername, setWpUsername] = useState('');
  const [wpAppPassword, setWpAppPassword] = useState('');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Pré-remplissage à la ré-ouverture d'un connecteur déjà actif (S — réparation 29 mai).
  // On récupère la config existante (jamais les credentials) pour ne plus tout retaper.
  const [isLoadingConfig, setIsLoadingConfig] = useState(false);
  const [hasStoredCredentials, setHasStoredCredentials] = useState(false);
  const [loadedConfig, setLoadedConfig] = useState({}); // préserve les clés config non éditées (admin_api_endpoint, site_url…)

  useEffect(() => {
    if (!alreadyActive) return;
    let cancelled = false;
    (async () => {
      setIsLoadingConfig(true);
      try {
        const token = localStorage.getItem('oracle_token');
        const res = await axios.get(
          `/api/connectors/projects/${projectId}/${connectorCode}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (cancelled) return;
        const c = res.data?.connector || {};
        const cfg = c.config || {};
        setLoadedConfig(cfg);
        setHasStoredCredentials(Boolean(c.has_credentials));
        // Pré-remplir les champs config selon le type (credentials volontairement omis)
        if (connectorCode === 'site_internet') {
          if (cfg.base_url) setBaseUrl(cfg.base_url);
          if (cfg.site_type) setSiteType(cfg.site_type);
          if (cfg.default_rubrique) setDefaultRubrique(cfg.default_rubrique);
        } else if (connectorCode === 'n8n') {
          if (cfg.webhook_url) setWebhookUrl(cfg.webhook_url);
        } else if (connectorCode === 'email') {
          if (cfg.imap_host) setImapHost(cfg.imap_host);
          if (cfg.imap_port) setImapPort(cfg.imap_port);
          if (typeof cfg.imap_secure === 'boolean') setImapSecure(cfg.imap_secure);
          if (cfg.smtp_host) setSmtpHost(cfg.smtp_host);
          if (cfg.smtp_port) setSmtpPort(cfg.smtp_port);
          if (typeof cfg.smtp_secure === 'boolean') setSmtpSecure(cfg.smtp_secure);
          if (cfg.from_email) setFromEmail(cfg.from_email);
          if (cfg.from_name) setFromName(cfg.from_name);
          if (cfg.signature_html) setSignatureHtml(cfg.signature_html);
          if (cfg.tls_servername) setTlsServername(cfg.tls_servername);
          if (cfg.imap_user) setImapUser(cfg.imap_user); // l'identifiant n'est pas un secret
        } else if (connectorCode === 'wordpress_source') {
          if (cfg.base_url) setBaseUrl(cfg.base_url);
          if (cfg.access_mode) setAccessMode(cfg.access_mode);
          if (cfg.wp_username) setWpUsername(cfg.wp_username);
        }
      } catch (err) {
        if (!cancelled) {
          // Non bloquant : si le détail échoue, on retombe sur des champs vides (comportement V1)
          console.warn('[ConnectorModal] préchargement config échoué', err?.response?.data || err.message);
        }
      } finally {
        if (!cancelled) setIsLoadingConfig(false);
      }
    })();
    return () => { cancelled = true; };
  }, [alreadyActive, projectId, connectorCode]);

  // S2 — Bouton "Tester la connexion"
  const [isTesting, setIsTesting] = useState(false);
  const [healthResult, setHealthResult] = useState(null);

  const isSiteInternet = connectorCode === 'site_internet';
  const isN8N = connectorCode === 'n8n';
  const isEmail = connectorCode === 'email';
  const isWordpress = connectorCode === 'wordpress_source';

  // Construction du body POST selon le type de connecteur
  const buildActivationBody = () => {
    if (isEmail) {
      const body = {
        auth_type: 'token',
        config: {
          ...loadedConfig,
          imap_host: imapHost.trim(),
          imap_port: Number(imapPort) || 993,
          imap_secure: imapSecure,
          smtp_host: smtpHost.trim(),
          smtp_port: Number(smtpPort) || 465,
          smtp_secure: smtpSecure,
          from_email: fromEmail.trim(),
          from_name: fromName.trim() || undefined,
          signature_html: signatureHtml.trim() || undefined,
          tls_servername: tlsServername.trim() || undefined,
        },
      };
      // Credentials envoyés UNIQUEMENT si re-saisis (sinon le backend conserve le token via COALESCE).
      if (imapUser.trim() && imapPassword) {
        body.credentials = { imap_user: imapUser.trim(), imap_password: imapPassword };
      }
      return body;
    }
    if (isSiteInternet) {
      const body = {
        auth_type: 'token',
        config: {
          ...loadedConfig,
          base_url: baseUrl.trim().replace(/\/+$/, ''),
          site_type: siteType,
          default_rubrique: defaultRubrique.trim() || undefined,
        },
      };
      if (apiUsername.trim() && apiPassword) {
        body.credentials = { api_username: apiUsername.trim(), api_password: apiPassword };
      }
      return body;
    }
    if (isWordpress) {
      const hasAppPw = Boolean(wpUsername.trim() && wpAppPassword.trim());
      const body = {
        auth_type: hasAppPw ? 'token' : 'workflow_only',
        config: {
          ...loadedConfig,
          base_url: baseUrl.trim().replace(/\/+$/, ''),
          access_mode: accessMode,
        },
      };
      if (hasAppPw) {
        body.credentials = { wp_username: wpUsername.trim(), wp_app_password: wpAppPassword.trim() };
      }
      return body;
    }
    // N8N par défaut
    return {
      auth_type: 'token',
      config: { ...loadedConfig, webhook_url: webhookUrl.trim() },
    };
  };

  const buildHealthPreviewBody = () => {
    const activation = buildActivationBody();
    return {
      preview: {
        auth_type: activation.auth_type,
        config: activation.config,
        credentials: activation.credentials || null,
      },
    };
  };

  // Sur un connecteur déjà actif avec credentials stockés, les identifiants
  // deviennent optionnels (laisser vide = conserver le token existant).
  const credsOptional = alreadyActive && hasStoredCredentials;
  const canSubmit = () => {
    if (isEmail) {
      const base = Boolean(imapHost && smtpHost && fromEmail);
      return credsOptional ? base : base && Boolean(imapUser && imapPassword);
    }
    if (isSiteInternet) {
      return credsOptional ? Boolean(baseUrl) : Boolean(baseUrl && apiUsername && apiPassword);
    }
    if (isWordpress) {
      if (!baseUrl) return false;
      if (accessMode !== 'rest_app_password') return true;
      return credsOptional ? true : Boolean(wpUsername && wpAppPassword);
    }
    return Boolean(webhookUrl);
  };

  const handleTest = async () => {
    if (!canSubmit()) return;
    setIsTesting(true);
    setHealthResult(null);
    try {
      const token = localStorage.getItem('oracle_token');
      const res = await axios.post(
        `/api/connectors/projects/${projectId}/${connectorCode}/health`,
        buildHealthPreviewBody(),
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setHealthResult(res.data || { ok: false, error: 'Réponse vide' });
    } catch (err) {
      setHealthResult({
        ok: false,
        error: err.response?.data?.error || err.message || 'Test échoué',
      });
    } finally {
      setIsTesting(false);
    }
  };

  // Styles partagés (extraction pour réutilisation dans les formulaires multi-types)
  const fieldLabelStyle = {
    display: 'block',
    marginBottom: 6,
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--text-secondary)',
  };
  const fieldInputStyle = {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid var(--card-border)',
    background: 'var(--input-bg, var(--card-bg))',
    color: 'var(--text)',
    fontSize: 13,
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'var(--pd-font-mono, monospace)',
    marginBottom: 4,
  };
  const fieldHintStyle = {
    fontSize: 11,
    color: 'var(--text-muted, #6a6a85)',
    margin: '6px 0 14px',
    lineHeight: 1.5,
  };
  const inlineCodeStyle = {
    background: 'var(--input-bg)',
    padding: '1px 5px',
    borderRadius: 3,
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit()) return;
    setIsSubmitting(true);
    setError(null);

    try {
      const token = localStorage.getItem('oracle_token');
      await axios.post(
        `/api/connectors/projects/${projectId}/${connectorCode}`,
        buildActivationBody(),
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSuccess(true);
      setTimeout(() => {
        if (onActivated) onActivated();
        onClose();
      }, 1800);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Activation échouée');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        backdropFilter: 'blur(4px)',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--modal-bg, var(--card-bg))',
          borderRadius: 14,
          padding: 28,
          maxWidth: 480,
          width: '90%',
          border: '1px solid var(--card-border)',
          boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              background: 'rgba(29,78,216,0.1)',
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
            }}
          >
            <Zap size={20} color="var(--accent, #1D4ED8)" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ margin: 0, fontSize: 17, color: 'var(--text)', fontWeight: 600 }}>
              {alreadyActive ? `Connecteur ${connectorName} actif` : `Activer le connecteur ${connectorName}`}
            </h2>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--text-muted, #6a6a85)' }}>
              {alreadyActive
                ? 'Tu peux soumettre de nouveaux paramètres pour rotater le token, ou déconnecter le service de ce projet.'
                : 'Une fois activé, demande au chat IA de déclencher tes workflows.'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            style={{
              background: 'none',
              border: 'none',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              color: 'var(--text-muted)',
              padding: 4,
              opacity: isSubmitting ? 0.4 : 1,
            }}
            aria-label="Fermer"
          >
            <X size={20} />
          </button>
        </div>

        {success ? (
          <div
            style={{
              padding: 16,
              borderRadius: 8,
              background: 'rgba(16,185,129,0.1)',
              color: '#10b981',
              textAlign: 'center',
              fontSize: 14,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
            }}
          >
            <CheckCircle2 size={18} />
            Connecteur activé — déclenche-le via le chat IA
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {isLoadingConfig && (
              <p style={{ ...fieldHintStyle, margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 6 }}>
                <Activity size={13} /> Chargement de la configuration existante…
              </p>
            )}
            {isN8N && (
              <>
                <label style={fieldLabelStyle}>Webhook URL</label>
                <input
                  type="url"
                  value={webhookUrl}
                  onChange={(e) => setWebhookUrl(e.target.value)}
                  placeholder="http://n8n:5678/webhook/..."
                  required
                  autoFocus
                  disabled={isSubmitting}
                  style={fieldInputStyle}
                />
                <p style={fieldHintStyle}>
                  💡 Depuis ITRIIA, utilise <code style={inlineCodeStyle}>http://n8n:5678</code> (réseau Docker interne) plutôt que <code>localhost</code>.
                </p>
              </>
            )}

            {isWordpress && (
              <>
                <label style={fieldLabelStyle}>URL du site WordPress</label>
                <input
                  type="url"
                  value={baseUrl}
                  onChange={(e) => setBaseUrl(e.target.value)}
                  placeholder="https://euroalgerie.org"
                  required
                  autoFocus
                  disabled={isSubmitting}
                  style={fieldInputStyle}
                />
                <p style={fieldHintStyle}>
                  📖 Lecture seule — ITRIIA n'écrit jamais sur ce site (inventaire, contenu, migration).
                </p>

                <label style={fieldLabelStyle}>Mode d'accès</label>
                <select
                  value={accessMode}
                  onChange={(e) => setAccessMode(e.target.value)}
                  disabled={isSubmitting}
                  style={{ ...fieldInputStyle, fontFamily: 'inherit' }}
                >
                  <option value="rest_public">REST publique (site ouvert)</option>
                  <option value="rest_app_password">Application Password (site sécurisé)</option>
                </select>
                <p style={fieldHintStyle}>
                  Si la REST est restreinte (plugin de sécurité type Solid Security), choisis « Application Password ».
                </p>

                {accessMode === 'rest_app_password' && (
                  <>
                    <label style={fieldLabelStyle}>Identifiant WordPress</label>
                    <input
                      type="text"
                      value={wpUsername}
                      onChange={(e) => setWpUsername(e.target.value)}
                      placeholder="ton.email@exemple.com"
                      disabled={isSubmitting}
                      style={fieldInputStyle}
                    />
                    <label style={fieldLabelStyle}>Application Password</label>
                    <input
                      type="password"
                      value={wpAppPassword}
                      onChange={(e) => setWpAppPassword(e.target.value)}
                      placeholder={credsOptional ? '•••• (laisser vide = conserver)' : 'xxxx xxxx xxxx xxxx xxxx xxxx'}
                      disabled={isSubmitting}
                      style={fieldInputStyle}
                    />
                    <p style={fieldHintStyle}>
                      WordPress → Utilisateurs → Profil → « Mots de passe d'application ». Jamais ton mot de passe de compte.
                    </p>
                  </>
                )}
              </>
            )}

            {isSiteInternet && (
              <>
                <label style={fieldLabelStyle}>URL du site</label>
                <input
                  type="url"
                  value={baseUrl}
                  onChange={(e) => setBaseUrl(e.target.value)}
                  placeholder="https://lavoixdorchies.fr"
                  required
                  autoFocus
                  disabled={isSubmitting}
                  style={fieldInputStyle}
                />
                <p style={fieldHintStyle}>
                  L'URL racine du site, sans <code style={inlineCodeStyle}>/admin</code> ni slash final.
                </p>

                <label style={fieldLabelStyle}>Type de site</label>
                <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                  {['mosquee', 'webradio', 'formation'].map(opt => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setSiteType(opt)}
                      disabled={isSubmitting}
                      style={{
                        flex: 1,
                        padding: '7px 10px',
                        borderRadius: 8,
                        border: '1px solid ' + (siteType === opt ? 'var(--accent, #1D4ED8)' : 'var(--card-border)'),
                        background: siteType === opt ? 'rgba(29,78,216,0.10)' : 'transparent',
                        color: 'var(--text)',
                        cursor: 'pointer',
                        fontSize: 12,
                        textTransform: 'capitalize',
                      }}
                    >
                      {SITE_TYPE_LABELS[opt] || opt}
                    </button>
                  ))}
                </div>

                <label style={fieldLabelStyle}>Utilisateur admin{credsOptional ? ' (laisser vide pour conserver)' : ''}</label>
                <input
                  type="text"
                  value={apiUsername}
                  onChange={(e) => setApiUsername(e.target.value)}
                  required={!credsOptional}
                  placeholder={credsOptional ? '•••• déjà enregistré' : ''}
                  disabled={isSubmitting}
                  style={fieldInputStyle}
                  autoComplete="username"
                />

                <label style={{ ...fieldLabelStyle, marginTop: 10 }}>Mot de passe admin{credsOptional ? ' (laisser vide pour conserver)' : ''}</label>
                <input
                  type="password"
                  value={apiPassword}
                  onChange={(e) => setApiPassword(e.target.value)}
                  required={!credsOptional}
                  placeholder={credsOptional ? '•••••••• déjà enregistré' : ''}
                  disabled={isSubmitting}
                  style={fieldInputStyle}
                  autoComplete="current-password"
                />
                <p style={fieldHintStyle}>
                  {credsOptional
                    ? '🔐 Token déjà en place. Laisse ces deux champs vides pour le conserver, ou renseigne-les pour le renouveler.'
                    : '🔐 Le mot de passe est échangé contre un token serveur-à-serveur (HMAC 24h) et jamais persisté.'}
                </p>

                <label style={fieldLabelStyle}>Rubrique par défaut (optionnel)</label>
                <input
                  type="text"
                  value={defaultRubrique}
                  onChange={(e) => setDefaultRubrique(e.target.value)}
                  placeholder="vie-citoyenne"
                  disabled={isSubmitting}
                  style={fieldInputStyle}
                />
              </>
            )}

            {isEmail && (
              <>
                <div style={{ display: 'flex', gap: 10 }}>
                  <div style={{ flex: 2 }}>
                    <label style={fieldLabelStyle}>Serveur IMAP (réception)</label>
                    <input
                      type="text"
                      value={imapHost}
                      onChange={(e) => setImapHost(e.target.value)}
                      placeholder="mail.lavoixdorchies.fr"
                      required
                      autoFocus
                      disabled={isSubmitting}
                      style={fieldInputStyle}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={fieldLabelStyle}>Port</label>
                    <input
                      type="number"
                      value={imapPort}
                      onChange={(e) => setImapPort(e.target.value)}
                      disabled={isSubmitting}
                      style={fieldInputStyle}
                    />
                  </div>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text-secondary)', margin: '0 0 12px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={imapSecure} onChange={(e) => setImapSecure(e.target.checked)} disabled={isSubmitting} />
                  TLS/SSL (port 993)
                </label>

                <div style={{ display: 'flex', gap: 10 }}>
                  <div style={{ flex: 2 }}>
                    <label style={fieldLabelStyle}>Serveur SMTP (envoi)</label>
                    <input
                      type="text"
                      value={smtpHost}
                      onChange={(e) => setSmtpHost(e.target.value)}
                      placeholder="mail.lavoixdorchies.fr"
                      required
                      disabled={isSubmitting}
                      style={fieldInputStyle}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={fieldLabelStyle}>Port</label>
                    <input
                      type="number"
                      value={smtpPort}
                      onChange={(e) => setSmtpPort(e.target.value)}
                      disabled={isSubmitting}
                      style={fieldInputStyle}
                    />
                  </div>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text-secondary)', margin: '0 0 12px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={smtpSecure} onChange={(e) => setSmtpSecure(e.target.checked)} disabled={isSubmitting} />
                  TLS/SSL (port 465)
                </label>

                <label style={fieldLabelStyle}>Adresse expéditeur</label>
                <input
                  type="email"
                  value={fromEmail}
                  onChange={(e) => setFromEmail(e.target.value)}
                  placeholder="contact@lavoixdorchies.fr"
                  required
                  disabled={isSubmitting}
                  style={fieldInputStyle}
                />

                <label style={{ ...fieldLabelStyle, marginTop: 10 }}>Nom expéditeur (optionnel)</label>
                <input
                  type="text"
                  value={fromName}
                  onChange={(e) => setFromName(e.target.value)}
                  placeholder="La Voix d'Orchies"
                  disabled={isSubmitting}
                  style={fieldInputStyle}
                />

                <label style={{ ...fieldLabelStyle, marginTop: 10 }}>Identifiant (souvent l'adresse complète){credsOptional ? ' (laisser vide pour conserver)' : ''}</label>
                <input
                  type="text"
                  value={imapUser}
                  onChange={(e) => setImapUser(e.target.value)}
                  placeholder={credsOptional ? '•••• déjà enregistré' : 'contact@lavoixdorchies.fr'}
                  required={!credsOptional}
                  disabled={isSubmitting}
                  style={fieldInputStyle}
                  autoComplete="username"
                />

                <label style={{ ...fieldLabelStyle, marginTop: 10 }}>Mot de passe{credsOptional ? ' (laisser vide pour conserver)' : ''}</label>
                <input
                  type="password"
                  value={imapPassword}
                  onChange={(e) => setImapPassword(e.target.value)}
                  required={!credsOptional}
                  placeholder={credsOptional ? '•••••••• déjà enregistré' : ''}
                  disabled={isSubmitting}
                  style={fieldInputStyle}
                  autoComplete="current-password"
                />
                <p style={fieldHintStyle}>
                  {credsOptional
                    ? '🔐 Identifiants déjà chiffrés côté serveur. Laisse vide pour les conserver, ou renseigne-les pour les remplacer.'
                    : '🔐 Les identifiants sont chiffrés (AES-256-GCM) et jamais renvoyés à l’interface. L’IA prépare des brouillons ; l’envoi reste un clic explicite.'}
                </p>

                <label style={{ ...fieldLabelStyle, marginTop: 4 }}>Signature HTML (optionnel)</label>
                <textarea
                  value={signatureHtml}
                  onChange={(e) => setSignatureHtml(e.target.value)}
                  placeholder="<p>—<br>La Voix d'Orchies</p>"
                  disabled={isSubmitting}
                  rows={2}
                  style={{ ...fieldInputStyle, resize: 'vertical' }}
                />

                <label style={{ ...fieldLabelStyle, marginTop: 10 }}>Nom du certificat TLS (avancé, optionnel)</label>
                <input
                  type="text"
                  value={tlsServername}
                  onChange={(e) => setTlsServername(e.target.value)}
                  placeholder="itriia.canalmoo.org"
                  disabled={isSubmitting}
                  style={fieldInputStyle}
                />
                <p style={fieldHintStyle}>
                  À remplir uniquement si le test échoue avec une erreur de type <code style={inlineCodeStyle}>Hostname/IP does not match certificate</code> : indiquez ici le nom porté par le certificat du serveur (souvent le hostname du serveur mail).
                </p>
              </>
            )}

            {healthResult && (
              <div
                style={{
                  padding: 10,
                  borderRadius: 6,
                  background: healthResult.ok ? 'rgba(16,185,129,0.1)' : 'rgba(220,38,38,0.1)',
                  color: healthResult.ok ? '#10b981' : '#dc2626',
                  fontSize: 12,
                  marginBottom: 14,
                  fontFamily: 'var(--pd-font-mono, monospace)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  flexWrap: 'wrap',
                }}
              >
                {healthResult.imap !== undefined ? (
                  // Email : deux pastilles IMAP / SMTP
                  <>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: healthResult.imap ? '#10b981' : '#dc2626' }}>
                      {healthResult.imap ? <CheckCircle2 size={14} /> : <X size={14} />} IMAP {healthResult.imap ? 'OK' : 'KO'}
                    </span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: healthResult.smtp ? '#10b981' : '#dc2626' }}>
                      {healthResult.smtp ? <CheckCircle2 size={14} /> : <X size={14} />} SMTP {healthResult.smtp ? 'OK' : 'KO'}
                    </span>
                    {healthResult.error && <span style={{ flexBasis: '100%', color: '#dc2626' }}>{healthResult.error}</span>}
                  </>
                ) : healthResult.auth !== undefined ? (
                  <>
                    {healthResult.reachable ? <CheckCircle2 size={14} /> : <X size={14} />}
                    {healthResult.reachable
                      ? `Joignable — HTTP ${healthResult.status || '?'}`
                      : `Site injoignable`}
                    <span style={{ marginLeft: 8, color: healthResult.auth ? '#10b981' : '#dc2626' }}>
                      {healthResult.auth ? '· Auth OK' : '· Auth échouée'}
                    </span>
                    {healthResult.error && !healthResult.ok && (
                      <span style={{ flexBasis: '100%' }}>{healthResult.error}</span>
                    )}
                  </>
                ) : (
                  <>
                    {healthResult.ok ? <CheckCircle2 size={14} /> : <X size={14} />}
                    {healthResult.ok
                      ? `Joignable — HTTP ${healthResult.status || '?'}`
                      : `Échec : ${healthResult.error || 'inconnu'}`}
                  </>
                )}
              </div>
            )}

            {error && (
              <div
                style={{
                  padding: 10,
                  borderRadius: 6,
                  background: 'rgba(220,38,38,0.1)',
                  color: '#dc2626',
                  fontSize: 12,
                  marginBottom: 14,
                  fontFamily: 'var(--pd-font-mono, monospace)',
                }}
              >
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap' }}>
              {alreadyActive && (
                <button
                  type="button"
                  onClick={handleDeactivate}
                  disabled={isSubmitting || isDeactivating}
                  title="Soft delete : le connecteur est désactivé, le token chiffré est supprimé, mais l'audit log est conservé."
                  style={{
                    marginRight: 'auto',
                    padding: '8px 12px',
                    borderRadius: 8,
                    border: '1px solid rgba(220,38,38,0.4)',
                    background: 'transparent',
                    color: '#dc2626',
                    cursor: (isSubmitting || isDeactivating) ? 'not-allowed' : 'pointer',
                    fontSize: 13,
                    opacity: (isSubmitting || isDeactivating) ? 0.5 : 1,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <Unplug size={14} />
                  {isDeactivating ? 'Déconnexion…' : 'Déconnecter'}
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                style={{
                  padding: '8px 16px',
                  borderRadius: 8,
                  border: '1px solid var(--card-border)',
                  background: 'transparent',
                  color: 'var(--text)',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  fontSize: 13,
                  opacity: isSubmitting ? 0.5 : 1,
                }}
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleTest}
                disabled={isSubmitting || isTesting || !canSubmit()}
                title="Vérifie que le service est joignable sans persister la connexion"
                style={{
                  padding: '8px 12px',
                  borderRadius: 8,
                  border: '1px solid var(--card-border)',
                  background: 'transparent',
                  color: 'var(--text)',
                  cursor: (isSubmitting || isTesting || !canSubmit()) ? 'not-allowed' : 'pointer',
                  fontSize: 13,
                  opacity: (isSubmitting || isTesting || !canSubmit()) ? 0.5 : 1,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <Activity size={14} />
                {isTesting ? 'Test…' : 'Tester'}
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !canSubmit()}
                style={{
                  padding: '8px 16px',
                  borderRadius: 8,
                  border: 'none',
                  background: isSubmitting || !canSubmit() ? 'var(--card-border)' : 'var(--accent, #1D4ED8)',
                  color: 'white',
                  cursor: isSubmitting || !canSubmit() ? 'not-allowed' : 'pointer',
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                {isSubmitting
                  ? (alreadyActive ? 'Mise à jour…' : 'Activation…')
                  : (alreadyActive ? 'Mettre à jour' : 'Activer')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ConnectorActivationModal;
